import css from "../mjbc-2026.module.scss";

/**
 * The rollover on every button in this flow — the "blob effect".
 *
 * Four yellow discs sit below the button and rise into it on a stagger. On
 * their own they would arrive as four separate circles; the SVG filter below
 * is what fuses them into one wobbling mass as they overlap. That is the whole
 * trick, and it is why the filter and the discs are shipped together here
 * rather than being two unrelated pieces.
 *
 * The discs are hidden from assistive tech and from the pointer: they are
 * decoration sitting behind the label, and a hit test that landed on one of
 * them would swallow the click.
 */
export function Blobs() {
    return (
        <span className={css.blobs} aria-hidden="true">
            <span className={css.blobsInner}>
                <span className={css.blob}/>
                <span className={css.blob}/>
                <span className={css.blob}/>
                <span className={css.blob}/>
            </span>
        </span>
    );
}

/**
 * The gooey filter the discs are drawn through. One per page — the discs
 * reference it by id from the stylesheet — so this mounts once at the top of a
 * sequence rather than inside each button.
 *
 * How it fuses them: blur the discs until neighbours bleed into one another,
 * then run the result through a colour matrix that multiplies alpha by 21 and
 * subtracts 7. That drives anything more than a third opaque to fully opaque
 * and everything fainter to nothing, so the soft blur snaps back to a hard
 * edge — one that now wraps the merged silhouette instead of four circles.
 *
 * The blur is asymmetric, and that is the whole reason this works across the
 * five button sizes on this page. The effect is normally written with a single
 * stdDeviation around 10, which assumes a button about 200px wide: the discs
 * are 25% wide at 0/30/60/90%, so the gaps between them are 5% of the width,
 * and a round 10px blur bridges 10px of gap comfortably. These buttons run 310
 * to 380 wide, which opens those gaps to 19px, and at 10 they stopped bridging
 * — the button settled as four separate yellow ellipses with dark pinches
 * between them. Hence 14 across.
 *
 * Down is a different constraint: the manifest's submit is only 35px tall, and
 * blurring THAT by 14 pushes the whole disc under the alpha threshold and the
 * button fills with nothing at all. 4 is what the shortest button survives.
 *
 * The filter region is widened to 200% because the default is only 120% of the
 * bounding box, which is narrower than the blur reaches — the discs would come
 * back with their haloes sliced off square.
 *
 * `focusable="false"` is for IE/older Edge, which otherwise put a tab stop on
 * every inline <svg>; harmless elsewhere and cheap insurance.
 */
export function GooFilter() {
    return (
        <svg className={css.gooFilter} aria-hidden="true" focusable="false">
            <defs>
                <filter id="mjbc-goo" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="14 4"/>
                    <feColorMatrix
                        in="blur"
                        mode="matrix"
                        values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 21 -7"
                        result="goo"
                    />
                    <feBlend in="SourceGraphic" in2="goo"/>
                </filter>
            </defs>
        </svg>
    );
}
